import { inject, Service } from "@angular/core";
import { Chart } from "chart.js";
import * as htmlToImage from "html-to-image";

import { LoaderService } from "./loader-service";
export interface ColumnConfig<T> {
  key: string; // dot notation supported
  label: string;
  width?: string;
  render?: (row: T) => string;
}

export interface PrintTableOptions<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  title?: string;
  logoUrl?: string;
  rtl?: boolean;
  indentKey?: string; // key in T that contains children
  maxIndentLevel?: number;
  customStyles?: string;
  rowFormatter?: (row: T, level: number) => string;
  rowHeaderIndex?: number; // index of column to be rendered as row header
  idLabelMap?: Record<string | number, string>; // map for ID -> label substitution
}

@Service()
export class PrintService {
  private _loaderService = inject(LoaderService);
  public async printPage(): Promise<void> {
    this._loaderService.start();
    await this._convertChartsToImages(); // Convert charts to images
    await this._convertSlidersToImages(); // Convert sliders to images

    const page = document.querySelector(".page-content") as HTMLElement;
    if (!page) return;

    // Create an iframe to render the content for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "100%";
    iframe.style.height = "auto"; // Automatically adjust height to fit the content
    iframe.style.border = "none";
    iframe.style.top = "-9999px"; // Hide iframe offscreen
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) return;

    // Clone the content of the page and insert into iframe
    const clonedContent = page.cloneNode(true) as HTMLElement;

    const elementToRemove = clonedContent.querySelector("adaa-float-action");
    if (elementToRemove) {
      elementToRemove.remove(); // Remove the element from the cloned content
    }

    iframeDoc.body.appendChild(clonedContent);

    // Copy styles from the main document to iframe to preserve the layout and styling
    const styles = Array.from(document.querySelectorAll("link, style"));
    styles.forEach((style) => {
      iframeDoc.head.appendChild(style.cloneNode(true)); // Clone styles into iframe
    });

    // Remove any scroll in the iframe and ensure all content is visible
    iframeDoc.body.style.margin = "0";
    iframeDoc.body.style.padding = "0";
    iframeDoc.body.style.overflow = "visible"; // Remove scrollbars and ensure content is fully visible
    iframeDoc.body.style.height = `${clonedContent.scrollHeight}px`; // Set iframe body height to content's height

    // Ensure the content doesn't overflow
    clonedContent.style.overflow = "visible"; // Make sure cloned content doesn't have overflow

    // After content is inserted, trigger print
    setTimeout(() => {
      // Focus on iframe and trigger print dialog
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print(); // Open print dialog

      // Cleanup after print dialog is triggered
      setTimeout(() => {
        this._loaderService.end();
        window.location.reload();
      }, 500);
    }, 2000); // Delay to ensure content is rendered before print dialog
  }

  private async _convertChartsToImages(): Promise<void> {
    const canvasElements = Array.from(document.querySelectorAll("canvas")) as HTMLCanvasElement[];

    for (const canvas of canvasElements) {
      const chartInstance = Chart.getChart(canvas);

      if (chartInstance) {
        chartInstance.resize(); // Ensure correct size before capturing
        chartInstance.update();

        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)); // Wait for rendering
      }

      const img = document.createElement("img");

      // Capture exact size
      img.src = canvas.toDataURL("image/png");
      img.style.width = `${canvas.clientWidth}px`; // Force image inside container
      img.style.height = `${canvas.clientHeight}px`;
      img.style.maxWidth = "100%"; // Ensure it doesn’t overflow
      img.style.maxHeight = "100%";
      img.style.objectFit = "contain"; // Prevent stretching
      img.classList.add("chart-image");

      canvas.parentNode?.replaceChild(img, canvas);
    }
  }

  private async _convertSlidersToImages(): Promise<void> {
    const sliderElements = Array.from(document.querySelectorAll(".slider-container")) as HTMLElement[];

    for (const slider of sliderElements) {
      const img = document.createElement("img");

      // Optionally capture a screenshot (or manually render slider's appearance)
      img.src = await this._captureSliderAsImage(slider);
      img.style.width = `${slider.clientWidth}px`;
      img.style.height = `${slider.clientHeight}px`;
      img.style.maxWidth = "100%";
      img.style.maxHeight = "100%";
      img.classList.add("slider-image");

      slider.parentNode?.replaceChild(img, slider);
    }
  }

  private _captureSliderAsImage(slider: HTMLElement): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      // Use html2canvas to capture the slider element as an image
      htmlToImage
        .toSvg(slider)
        .then((dataUrl) => {
          resolve(dataUrl); // Return the image URL (base64)
        })
        .catch((error) => {
          reject(error); // Handle errors
        });
    });
  }

  public printTable<T>(options: PrintTableOptions<T>): void {
    const {
      data,
      columns,
      title,
      logoUrl,
      rtl = false,
      indentKey = "children",
      maxIndentLevel = 5,
      customStyles = "",
      rowHeaderIndex,
      idLabelMap = {},
    } = options;

    const direction = rtl ? "rtl" : "ltr";
    const indentPadding = (level: number) => `${Math.min(level, maxIndentLevel) * 20}px`;
    const getPrefixByLevel = (level: number): string => {
      switch (level) {
        case 1:
          return "• ";
        case 2:
          return "◦ ";
        case 3:
          return "‣ ";
        case 4:
          return "- ";
        default:
          return "";
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extractValue = (obj: any, path: string): any => {
      return path.split(".").reduce((acc, part) => acc?.[part], obj);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resolveLabel = (val: any): string => {
      if (val == null) return "";
      return idLabelMap[val] ?? val;
    };

    const renderRow = (row: T, level: number = 0): string => {
      const rowClass = `indent-${level}`;
      const cells = columns
        .map((col, i) => {
          const rawValue = col.render ? col.render(row) : extractValue(row, col.key);
          const value = resolveLabel(rawValue);
          const prefix = i === 0 && level > 0 ? getPrefixByLevel(level) : "";
          const displayValue = `${prefix}${value ?? ""}`;
          const cellTag = i === rowHeaderIndex ? "th" : "td";
          const style = i === 0 ? `padding-${direction === "rtl" ? "right" : "left"}: ${indentPadding(level)};` : "";
          return `<${cellTag} class="${rowClass}" style="${style}">${displayValue}</${cellTag}>`;
        })
        .join("");

      const rowHTML = `<tr>${cells}</tr>`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const children = (row as any)[indentKey] as T[];
      const childRows = Array.isArray(children) ? children.map((child) => renderRow(child, level + 1)).join("") : "";
      return `${rowHTML}${childRows}`;
    };

    const tableHTML = `
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            ${columns.map((col) => `<th style="text-align: ${rtl ? "right" : "left"}; width: ${col.width ?? "auto"}; border: 1px solid #000; padding: 6px;">${col.label}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${data.map((row) => renderRow(row)).join("")}
        </tbody>
      </table>
    `;

    const html = `
      <html dir="${direction}">
      <head>
        <meta charset="UTF-8">
        <title>${title ?? "Print Table"}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; direction: ${direction}; }
          table, th, td { border: 1px solid #000; }
          th, td { padding: 6px; }
          ${customStyles}
        </style>
      </head>
      <body>
        ${logoUrl ? `<div style="text-align: center;"><img src="${logoUrl}" style="max-height: 100px;" /></div>` : ""}
        ${title ? `<h2 style="text-align: center;">${title}</h2>` : ""}
        ${tableHTML}
      </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    };
  }
}
